import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/useToast';
import {
  useUserApiKeys,
  useCreateUserApiKey,
  useRevokeUserApiKey,
  useRotateUserApiKey,
  useApiKeyScopes,
} from '@/hooks/useAdmin';
import { Plus, Copy, Trash2, RotateCw } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { ApiKeyDisplayModal } from './ApiKeyDisplayModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { User, CreateAPIKeyResponse, APIKey } from '@/types';

interface ApiKeyManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export const ApiKeyManagementModal: React.FC<ApiKeyManagementModalProps> = ({
  open,
  onOpenChange,
  user,
}) => {
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expirationDays, setExpirationDays] = useState<string>('365');
  const [newApiKeyData, setNewApiKeyData] = useState<CreateAPIKeyResponse | null>(null);
  const [keyDisplayOpen, setKeyDisplayOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<APIKey | null>(null);

  const { toast } = useToast();
  const { data: apiKeys, isLoading } = useUserApiKeys(user?.id || '');
  const { data: scopes } = useApiKeyScopes();
  const createApiKey = useCreateUserApiKey();
  const revokeApiKey = useRevokeUserApiKey();
  const rotateApiKey = useRotateUserApiKey();

  const activeKeys = apiKeys?.filter((key) => !key.revoked) || [];
  const revokedKeys = apiKeys?.filter((key) => key.revoked) || [];

  const handleGenerateKey = async () => {
    if (!user || !newKeyName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a name for the API key',
        variant: 'destructive',
      });
      return;
    }

    if (selectedScopes.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one permission scope',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createApiKey.mutateAsync({
        userId: user.id,
        data: {
          name: newKeyName,
          description: newKeyDescription || undefined,
          scopes: selectedScopes,
          expires_in_days: expirationDays === 'never' ? undefined : parseInt(expirationDays),
        },
      });

      setNewApiKeyData(result);
      setKeyDisplayOpen(true);
      setIsGenerateOpen(false);
      setNewKeyName('');
      setNewKeyDescription('');
      setSelectedScopes([]);
      setExpirationDays('365');

      toast({
        title: 'Success',
        description: 'API key generated successfully',
      });
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate API key',
        variant: 'destructive',
      });
    }
  };

  const handleRevokeClick = (key: APIKey) => {
    setKeyToRevoke(key);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!user || !keyToRevoke) return;

    try {
      await revokeApiKey.mutateAsync({
        userId: user.id,
        keyId: keyToRevoke.id,
      });

      toast({
        title: 'Success',
        description: `API key "${keyToRevoke.name}" has been revoked`,
      });
      setRevokeDialogOpen(false);
      setKeyToRevoke(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to revoke API key',
        variant: 'destructive',
      });
    }
  };

  const handleRotate = async (key: APIKey) => {
    if (!user) return;

    if (
      !confirm(
        `Are you sure you want to rotate "${key.name}"? The old key will stop working immediately.`
      )
    ) {
      return;
    }

    try {
      const result = await rotateApiKey.mutateAsync({
        userId: user.id,
        keyId: key.id,
      });

      setNewApiKeyData(result);
      setKeyDisplayOpen(true);

      toast({
        title: 'Success',
        description: 'API key rotated successfully',
      });
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to rotate API key',
        variant: 'destructive',
      });
    }
  };

  const handleCopy = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast({
        title: 'Copied to clipboard',
        description: 'API key prefix has been copied',
      });
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  if (!user) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage API Keys - {user.full_name || user.email}</DialogTitle>
            <DialogDescription>{user.email}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Generate Key Button */}
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">
                Active API Keys ({activeKeys.length})
              </h3>
              <Button size="sm" onClick={() => setIsGenerateOpen(!isGenerateOpen)}>
                <Plus className="h-4 w-4 mr-2" />
                Generate Key
              </Button>
            </div>

            {/* Generate Key Form */}
            {isGenerateOpen && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Generate New API Key</CardTitle>
                  <CardDescription>
                    Create a new API key with specific permissions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="key-name">Key Name *</Label>
                    <Input
                      id="key-name"
                      placeholder="Production Key"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="key-description">Description</Label>
                    <Input
                      id="key-description"
                      placeholder="Purpose of this API key"
                      value={newKeyDescription}
                      onChange={(e) => setNewKeyDescription(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Scopes (Permissions) *</Label>
                    <div className="space-y-3">
                      {(() => {
                        const scopeItems = scopes && scopes.length > 0
                          ? [...scopes].map((s) => ({ key: s.id, name: s.name }))
                          : ['datasets:read', 'datasets:write', 'ingestion:execute', 'views:read']
                              .sort()
                              .map((s) => ({ key: s, name: s }));

                        // Group scopes by prefix (part before ':')
                        const groups: Record<string, typeof scopeItems> = {};
                        for (const item of scopeItems) {
                          const prefix = item.name.includes(':') ? item.name.split(':')[0] : 'other';
                          const groupKey = prefix === '*' ? 'global' : prefix;
                          if (!groups[groupKey]) groups[groupKey] = [];
                          groups[groupKey].push(item);
                        }

                        // Sort items within each group alphabetically
                        for (const key of Object.keys(groups)) {
                          groups[key].sort((a, b) => a.name.localeCompare(b.name));
                        }

                        // Sort group keys: 'global' first, then alphabetical
                        const sortedKeys = Object.keys(groups).sort((a, b) => {
                          if (a === 'global') return -1;
                          if (b === 'global') return 1;
                          return a.localeCompare(b);
                        });

                        const groupLabels: Record<string, string> = {
                          global: 'Global',
                          admin: 'Admin',
                          api_keys: 'API Keys',
                          connector: 'Connector',
                          datasets: 'Datasets',
                          files: 'Files',
                          ingestion: 'Ingestion',
                          lineage: 'Lineage',
                          metadata: 'Metadata',
                          permissions: 'Permissions',
                          query: 'Query',
                          quality: 'Quality',
                          schemas: 'Schemas',
                          users: 'Users',
                          views: 'Views',
                        };

                        return sortedKeys.map((groupKey) => (
                          <div key={groupKey}>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                              {groupLabels[groupKey] || groupKey}
                            </p>
                            <div className="grid grid-cols-2 gap-1">
                              {groups[groupKey].map((item) => (
                                <div key={item.key} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={item.key}
                                    checked={selectedScopes.includes(item.name)}
                                    onCheckedChange={() => toggleScope(item.name)}
                                  />
                                  <label
                                    htmlFor={item.key}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {item.name}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Expiration</Label>
                    <RadioGroup value={expirationDays} onValueChange={setExpirationDays}>
                      <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="30" id="exp-30" />
                          <Label htmlFor="exp-30">30 days</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="90" id="exp-90" />
                          <Label htmlFor="exp-90">90 days</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="365" id="exp-365" />
                          <Label htmlFor="exp-365">1 year</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="never" id="exp-never" />
                          <Label htmlFor="exp-never">Never</Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsGenerateOpen(false);
                        setNewKeyName('');
                        setNewKeyDescription('');
                        setSelectedScopes([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleGenerateKey}
                      disabled={createApiKey.isPending}
                    >
                      {createApiKey.isPending ? 'Generating...' : 'Generate Key'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Keys List */}
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading API keys...</div>
            ) : activeKeys.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No active API keys</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeKeys.map((key) => (
                  <Card key={key.id}>
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{key.name}</h4>
                              <Badge variant="default">Active</Badge>
                            </div>
                            {key.description && (
                              <p className="text-sm text-muted-foreground">{key.description}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">
                            {key.key_prefix}{'*'.repeat(32)}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(key.key_prefix)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium">Created:</span>{' '}
                            {formatDate(key.created_at)}
                          </div>
                          {key.last_used_at && (
                            <div>
                              <span className="font-medium">Last used:</span>{' '}
                              {formatDate(key.last_used_at)}
                            </div>
                          )}
                          {key.expires_at && (
                            <div>
                              <span className="font-medium">Expires:</span>{' '}
                              {formatDate(key.expires_at)}
                            </div>
                          )}
                          <div className="col-span-2">
                            <span className="font-medium">Scopes:</span> {[...key.scopes].sort().join(', ')}
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRotate(key)}
                            disabled={rotateApiKey.isPending}
                          >
                            <RotateCw className="h-4 w-4 mr-1" />
                            Rotate
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRevokeClick(key)}
                            disabled={revokeApiKey.isPending}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Revoke
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Revoked Keys Section */}
            {revokedKeys.length > 0 && (
              <details className="border rounded-lg p-4">
                <summary className="cursor-pointer font-medium text-sm">
                  Revoked Keys ({revokedKeys.length})
                </summary>
                <div className="mt-4 space-y-2">
                  {revokedKeys.map((key) => (
                    <div
                      key={key.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-md text-sm"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{key.name}</span>
                          <Badge variant="secondary">Revoked</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <code>{key.key_prefix}{'*'.repeat(32)}</code>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Revoked: {formatDate(key.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* API Key Display Modal */}
      <ApiKeyDisplayModal
        open={keyDisplayOpen}
        onOpenChange={setKeyDisplayOpen}
        apiKeyData={newApiKeyData}
        userEmail={user.email}
        userName={user.full_name}
      />

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        title="Revoke API Key"
        description={
          keyToRevoke
            ? `Are you sure you want to revoke "${keyToRevoke.name}"? This will immediately stop all applications using this key from accessing the API.`
            : ''
        }
        onConfirm={handleRevokeConfirm}
        confirmText="Revoke"
        variant="destructive"
        loading={revokeApiKey.isPending}
      />
    </>
  );
};
